import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Linkedin, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../mock";
import { newsletterApi } from "../api";
import { useTurnstile } from "./Turnstile";
import ContactSection from "./ContactSection";

export function NewsletterForm({ dark = true, id = "newsletter" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { containerRef, getToken, resetToken, configured } = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      if (configured && !token) {
        toast.error("Verification could not be completed. Please refresh the page and try again.");
        return;
      }
      const res = await newsletterApi.subscribe(trimmedName, trimmedEmail, token);
      if (res.subscribed) {
        resetToken();
        toast.success("Welcome aboard! You're subscribed to our newsletter.");
        setName("");
        setEmail("");
      } else {
        toast.info("You're already subscribed with this email.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldBase = "px-4 py-3 text-sm outline-none border transition-colors duration-300 font-sans-d";
  const fieldTheme = dark
    ? "bg-[#0a1826] border-[#1d3a54] text-white placeholder-[#5b7a88] focus:border-[#29abe2]"
    : "bg-white border-[#d7e3ec] text-[#1c2b39] placeholder-[#8aa0b0] focus:border-[#29abe2]";

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row w-full max-w-lg gap-2 sm:gap-0" data-testid="newsletter-form" id={id}>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name"
        required
        data-testid="newsletter-name-input"
        className={`${fieldBase} ${fieldTheme} sm:w-40 sm:border-r-0`}
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        required
        data-testid="newsletter-email-input"
        className={`${fieldBase} ${fieldTheme} flex-1`}
      />
      <button type="submit" disabled={loading} aria-label="Subscribe to the newsletter" className="gee-btn !px-5" data-testid="newsletter-submit-btn">
        {loading ? "..." : <ArrowRight size={18} />}
      </button>
      <div ref={containerRef} className="basis-full" />
    </form>
  );
}

export default function Footer() {
  return (
    <>
      <ContactSection />
      <footer style={{ backgroundColor: "#081826" }} data-testid="site-footer">
      {/* Newsletter band */}
      <div style={{ backgroundColor: "#0f2438" }} className="border-b border-[#16344e] border-t border-t-[#16344e]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <p className="gee-kicker mb-2">Newsletter</p>
            <h3 className="font-serif-d text-white text-2xl md:text-3xl">
              Stay ahead of the field
            </h3>
            <p className="text-[#7d94a6] mt-2 max-w-md">
              Race results, foal photos and insider insights — straight to your inbox.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <Link to="/">
            <img src={IMAGES.logo} alt="Global Equine Group" width="345" height="66" loading="lazy" className="h-12 w-auto mb-6" />
          </Link>
          <p className="text-[#7d94a6] text-sm leading-relaxed max-w-xs">
            A unique racing management company. Bespoke, unique and adaptable —
            with you every step of the way.
          </p>
          <div className="flex items-center gap-5 mt-6">
            <a href="https://www.instagram.com/global_equine_group/" target="_blank" rel="noreferrer" aria-label="Instagram" className="text-[#29abe2] hover:text-white transition-colors"><Instagram size={20} strokeWidth={1.8} /></a>
            <a href="https://x.com/globalequine" target="_blank" rel="noreferrer" aria-label="X (formerly Twitter)" className="text-[#29abe2] hover:text-white transition-colors"><Twitter size={20} strokeWidth={1.8} /></a>
            <a href="https://www.linkedin.com/company/global-equine-group-ltd/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-[#29abe2] hover:text-white transition-colors"><Linkedin size={20} strokeWidth={1.8} /></a>
          </div>
        </div>

        <div>
          <p className="gee-kicker mb-5">Explore</p>
          <nav className="flex flex-col gap-3">
            {[
              { label: "Home", to: "/" },
              { label: "Racing Management", to: "/racing-management" },
              { label: "The Team", to: "/team" },
              { label: "Testimonials", to: "/testimonials" },
              { label: "Latest News", to: "/blog" },
              { label: "Videos", to: "/videos" },
            ].map((l) => (
              <Link key={l.label} to={l.to}
                className="text-[#b9c9d6] hover:text-[#29abe2] transition-colors text-sm w-fit">
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        <div>
          <p className="gee-kicker mb-5">Contact</p>
          <div className="flex flex-col gap-4 text-sm">
            <a href="mailto:info@globalequinegroup.com"
              className="flex items-center gap-3 text-[#b9c9d6] hover:text-[#29abe2] transition-colors w-fit">
              <Mail size={16} className="text-[#29abe2]" /> info@globalequinegroup.com
            </a>
            <span className="flex items-center gap-3 text-[#b9c9d6]">
              <MapPin size={16} className="text-[#29abe2]" /> Newmarket, Suffolk, England
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-[#16344e]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[#8aa8bc] text-xs">
            © {new Date().getFullYear()} Global Equine Group. All rights reserved.
          </p>
          <Link to="/admin" className="text-[#8aa8bc] hover:text-[#29abe2] text-xs transition-colors" data-testid="admin-link">
            Admin
          </Link>
        </div>
      </div>
    </footer>
    </>
  );
}
